/**
 * Store list: hours and coordinates come from metaobjects.
 * Google Maps JS loads only when the shopper uses address search
 * (autocomplete / geocode). "Nearby" uses browser geolocation only.
 */
(function () {
  const DATA_SELECTOR = "[data-stores-locator-data]";
  const DAY_TO_NUMBER = {
    sunday: 0,
    sun: 0,
    "0": 0,
    monday: 1,
    mon: 1,
    "1": 1,
    tuesday: 2,
    tue: 2,
    "2": 2,
    wednesday: 3,
    wed: 3,
    "3": 3,
    thursday: 4,
    thu: 4,
    "4": 4,
    friday: 5,
    fri: 5,
    "5": 5,
    saturday: 6,
    sat: 6,
    "6": 6,
    ראשון: 0,
    שני: 1,
    שלישי: 2,
    רביעי: 3,
    חמישי: 4,
    שישי: 5,
    שבת: 6,
  };

  let mapsApiPromise = null;

  function parseConfig(script) {
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return null;
    }
  }

  function parseCoordinate(value) {
    if (value == null || value === "") return null;

    const normalized = String(value).trim().replace(",", ".");
    const parsed = parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  function readLatLng(location) {
    if (!location) return null;

    const lat =
      typeof location.lat === "function" ? location.lat() : location.lat;
    const lng =
      typeof location.lng === "function" ? location.lng() : location.lng;

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng };
  }

  function parseCoordsFromMapLink(url) {
    if (!url) return null;

    const patterns = [
      /!3d(-?\d+\.?\d*)!4d(-?\d+\.?\d*)/,
      /[?&]ll=(-?\d+\.?\d*),(-?\d+\.?\d*)/,
      /@(-?\d+\.?\d*),(-?\d+\.?\d*)/,
      /[?&]q=(-?\d+\.?\d*),(-?\d+\.?\d*)/,
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (!match) continue;

      const lat = parseFloat(match[1]);
      const lng = parseFloat(match[2]);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        return { lat, lng };
      }
    }

    return null;
  }

  function hasValidCoords(store) {
    return (
      Number.isFinite(store.lat) &&
      Number.isFinite(store.lng) &&
      (store.lat !== 0 || store.lng !== 0)
    );
  }

  function normalizeStores(rawStores) {
    return (rawStores || []).map((store) => {
      const themeLat = parseCoordinate(store.lat);
      const themeLng = parseCoordinate(store.lng);
      const coords =
        themeLat != null && themeLng != null
          ? { lat: themeLat, lng: themeLng }
          : parseCoordsFromMapLink(store.mapLink);

      return {
        ...store,
        lat: coords?.lat ?? null,
        lng: coords?.lng ?? null,
        openingHours: parseOpeningHours(store.openingHours),
        businessStatus: String(store.businessStatus || "").trim(),
      };
    });
  }

  function parseTimeToHHmm(value) {
    const raw = String(value || "").trim();
    if (!raw) return null;

    const colon = raw.match(/^(\d{1,2}):(\d{2})$/);
    if (colon) {
      return colon[1].padStart(2, "0") + colon[2];
    }

    const digits = raw.replace(/\D/g, "");
    if (digits.length === 3 || digits.length === 4) {
      return digits.padStart(4, "0");
    }

    return null;
  }

  function parseRange(value) {
    const parts = String(value)
      .split(/-|–|—/)
      .map((part) => part.trim())
      .filter(Boolean);

    if (parts.length < 2) return null;

    const open = parseTimeToHHmm(parts[0]);
    const close = parseTimeToHHmm(parts[1]);
    if (!open || !close) return null;

    return { open, close };
  }

  function isClosedValue(value) {
    const normalized = String(value || "")
      .trim()
      .toLowerCase();
    return (
      !normalized ||
      normalized === "closed" ||
      normalized === "סגור" ||
      normalized === "close"
    );
  }

  function rangesToPeriods(day, value) {
    if (isClosedValue(value)) return [];

    return String(value)
      .split(/[,;]+/)
      .map((part) => part.trim())
      .filter(Boolean)
      .map((part) => parseRange(part))
      .filter(Boolean)
      .map((range) => {
        const openMinutes = timeToMinutes(range.open);
        const closeMinutes = timeToMinutes(range.close);
        let closeDay = day;
        if (
          Number.isFinite(openMinutes) &&
          Number.isFinite(closeMinutes) &&
          closeMinutes <= openMinutes
        ) {
          closeDay = (day + 1) % 7;
        }

        return {
          open: { day, time: range.open },
          close: { day: closeDay, time: range.close },
        };
      });
  }

  function parseDayKeyedHours(source) {
    const periods = [];

    Object.entries(source).forEach(([key, value]) => {
      const day = DAY_TO_NUMBER[String(key).trim().toLowerCase()];
      if (day == null) return;
      periods.push(...rangesToPeriods(day, value));
    });

    return periods.length ? { periods } : null;
  }

  function parseLineHours(text) {
    const source = {};

    String(text)
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .forEach((line) => {
        const match = line.match(/^([^:]+)\s*:\s*(.+)$/);
        if (!match) return;
        source[match[1].trim()] = match[2].trim();
      });

    return parseDayKeyedHours(source);
  }

  function parseOpeningHours(raw) {
    if (!raw) return null;

    let value = raw;
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) return null;

      try {
        value = JSON.parse(trimmed);
      } catch (error) {
        return parseLineHours(trimmed);
      }
    }

    if (Array.isArray(value)) {
      return value.length ? { periods: value } : null;
    }

    if (typeof value !== "object") return null;

    if (Array.isArray(value.periods)) {
      return value.periods.length ? { periods: value.periods } : null;
    }

    if (value.opening_hours?.periods) {
      return parseOpeningHours(value.opening_hours);
    }

    if (value.current_opening_hours?.periods) {
      return parseOpeningHours(value.current_opening_hours);
    }

    return parseDayKeyedHours(value);
  }

  function getClosedTodayDisplay(labels) {
    return {
      hoursText: labels.today + " " + labels.closedToday,
      statusText: labels.closed,
      isOpen: false,
    };
  }

  function formatGoogleTime(timeValue) {
    const normalized = String(timeValue).padStart(4, "0");
    return normalized.slice(0, 2) + ":" + normalized.slice(2, 4);
  }

  function timeToMinutes(timeValue) {
    const normalized = String(timeValue).padStart(4, "0");
    const hours = parseInt(normalized.slice(0, 2), 10);
    const minutes = parseInt(normalized.slice(2, 4), 10);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return NaN;
    return hours * 60 + minutes;
  }

  function getZonedParts(timeZone) {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      weekday: "long",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(new Date());

    const getPart = (type) => parts.find((part) => part.type === type)?.value;
    const weekday = String(getPart("weekday") || "").toLowerCase();

    return {
      day: DAY_TO_NUMBER[weekday],
      hour: parseInt(getPart("hour"), 10),
      minute: parseInt(getPart("minute"), 10),
    };
  }

  function computeOpenNowFromPeriods(periods, timeZone) {
    if (!Array.isArray(periods) || !periods.length) return null;

    const { day, hour, minute } = getZonedParts(timeZone);
    if (!Number.isFinite(day) || !Number.isFinite(hour) || !Number.isFinite(minute)) {
      return null;
    }

    const WEEK_MINUTES = 7 * 24 * 60;
    const now = day * 24 * 60 + hour * 60 + minute;

    for (const period of periods) {
      if (!period?.open) continue;
      if (!period.close) return true;

      const start = period.open.day * 24 * 60 + timeToMinutes(period.open.time);
      let end = period.close.day * 24 * 60 + timeToMinutes(period.close.time);
      if (!Number.isFinite(start) || !Number.isFinite(end)) continue;
      if (end <= start) end += WEEK_MINUTES;

      if (
        (now >= start && now < end) ||
        (now + WEEK_MINUTES >= start && now + WEEK_MINUTES < end)
      ) {
        return true;
      }
    }

    return false;
  }

  function getHoursDisplay(store, labels, timeZone) {
    if (!store) return null;

    if (
      store.businessStatus === "CLOSED_PERMANENTLY" ||
      store.businessStatus === "CLOSED_TEMPORARILY"
    ) {
      return getClosedTodayDisplay(labels);
    }

    const openingHours = store.openingHours;
    if (!openingHours?.periods?.length) return null;

    const today = getZonedParts(timeZone).day;
    const todayPeriods = openingHours.periods.filter(
      (period) => period.open?.day === today,
    );

    if (!todayPeriods.length) {
      return getClosedTodayDisplay(labels);
    }

    const hoursRanges = todayPeriods.map((period) => {
      const openTime = formatGoogleTime(period.open.time);
      if (!period.close) return openTime;
      return openTime + "-" + formatGoogleTime(period.close.time);
    });

    const computed = computeOpenNowFromPeriods(openingHours.periods, timeZone);

    return {
      hoursText: labels.today + " " + hoursRanges.join(", "),
      statusText: computed ? labels.open : labels.closed,
      isOpen: computed === true,
    };
  }

  function renderHoursElement(element, display) {
    if (!display) {
      element.hidden = true;
      element.classList.remove("is-loading");
      return;
    }

    element.classList.remove("is-loading");
    element.hidden = false;
    element.textContent = "";
    element.append(display.hoursText);

    const separator = document.createElement("span");
    separator.className = "store-card__hours-separator";
    separator.setAttribute("aria-hidden", "true");
    separator.textContent = "•";
    element.append(separator);

    const status = document.createElement("span");
    status.className = display.isOpen
      ? "store-card__status store-card__status--open"
      : "store-card__status store-card__status--closed";
    status.textContent = display.statusText;
    element.appendChild(status);
  }

  function calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  function formatDistance(km) {
    if (km < 1) {
      return Math.round(km * 1000) + " מ׳";
    }
    return km.toFixed(1) + " ק״מ";
  }

  function debounce(func, wait) {
    let timeout;
    return function debounced(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  function loadGoogleMapsApi(apiKey) {
    if (!apiKey) return Promise.reject(new Error("Missing Google Maps API key"));
    if (window.google?.maps?.places) return Promise.resolve();
    if (mapsApiPromise) return mapsApiPromise;

    mapsApiPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector("script[data-stores-places-api]");
      if (existing) {
        existing.addEventListener("load", () => resolve(), { once: true });
        existing.addEventListener("error", () => reject(new Error("Maps load failed")), {
          once: true,
        });
        return;
      }

      const callbackName = "initStoresLocatorPlaces";
      window[callbackName] = () => resolve();

      const script = document.createElement("script");
      script.dataset.storesPlacesApi = "true";
      script.src =
        "https://maps.googleapis.com/maps/api/js?key=" +
        encodeURIComponent(apiKey) +
        "&libraries=places&callback=" +
        callbackName;
      script.async = true;
      script.defer = true;
      script.addEventListener("error", () => reject(new Error("Maps load failed")), {
        once: true,
      });
      document.head.appendChild(script);
    });

    return mapsApiPromise;
  }

  function geocodeQuery(query) {
    return new Promise((resolve, reject) => {
      if (!window.google?.maps?.Geocoder) {
        reject(new Error("Geocoder unavailable"));
        return;
      }

      const geocoder = new google.maps.Geocoder();
      geocoder.geocode(
        { address: query, componentRestrictions: { country: "il" } },
        (results, status) => {
          if (status === "OK" && results?.[0]?.geometry?.location) {
            resolve(results[0].geometry.location);
            return;
          }

          reject(new Error(status));
        },
      );
    });
  }

  function initStoreSearch(config) {
    const container = document.querySelector(
      "#shopify-section-" + config.sectionId,
    );
    if (!container || container.dataset.storesSearchInit === "true") return;
    container.dataset.storesSearchInit = "true";

    const searchInput = document.getElementById(
      "stores-search-" + config.sectionId,
    );
    const noResultsEl = document.getElementById(
      "stores-no-results-" + config.sectionId,
    );
    const clearBtn = container.querySelector(".stores-search__clear");
    const geoBtn = document.getElementById("stores-geo-btn-" + config.sectionId);
    const cards = container.querySelectorAll(".store-card");
    const storesList = container.querySelector(".stores-list__items");
    const showAllBtn = container.querySelector("#show_all_stores_btn");
    const remainingWrap = container.querySelector("#the_remaining_stores");

    if (!searchInput || !cards.length || !storesList) return;

    const stores = normalizeStores(config.stores);
    const labels = config.labels || {};
    const timeZone = config.hoursTimezone || "Asia/Jerusalem";
    const apiKey = config.googleMapsApiKey || "";

    let searchMode = "text";
    let distanceUpdateToken = 0;
    let suppressTextSearchUntil = 0;
    let autocomplete = null;
    let googleReady = false;
    let userExpandedStores = Boolean(
      remainingWrap &&
        showAllBtn &&
        remainingWrap.style.display !== "none" &&
        showAllBtn.style.display === "none",
    );

    function getStoreCard(index) {
      return container.querySelector(
        '.store-card[data-store-index="' + index + '"]',
      );
    }

    function appendListControls() {
      if (showAllBtn) storesList.appendChild(showAllBtn);
      if (remainingWrap) storesList.appendChild(remainingWrap);
    }

    function expandAllStoresLayout() {
      if (remainingWrap) remainingWrap.style.display = "flex";
      if (showAllBtn) showAllBtn.style.display = "none";
    }

    function appendCardsInOrder(storeList) {
      storeList.forEach((store) => {
        const card = getStoreCard(store.index);
        if (card) storesList.appendChild(card);
      });
      appendListControls();
    }

    function restoreOriginalOrder() {
      const ordered = [...stores].sort((a, b) => a.index - b.index);

      if (userExpandedStores || !remainingWrap || !showAllBtn) {
        appendCardsInOrder(ordered);
        return;
      }

      ordered.forEach((store) => {
        const card = getStoreCard(store.index);
        if (!card) return;

        if (store.index < config.initialVisibleStores) {
          storesList.appendChild(card);
        } else {
          remainingWrap.appendChild(card);
        }
      });

      appendListControls();
      showAllBtn.style.display = "";
      remainingWrap.style.display = "none";
    }

    function clearDistances() {
      container.querySelectorAll("[data-distance-for]").forEach((el) => {
        el.hidden = true;
        el.textContent = "";
      });
    }

    function renderAllHours() {
      container.querySelectorAll("[data-store-hours]").forEach((element) => {
        const storeIndex = parseInt(element.dataset.storeIndex, 10);
        const store = stores.find((item) => item.index === storeIndex);
        const display = getHoursDisplay(store, labels, timeZone);
        renderHoursElement(element, display);
      });
    }

    function showAllStores() {
      container.querySelectorAll(".store-card").forEach((card) => {
        card.hidden = false;
      });
      if (noResultsEl) noResultsEl.style.display = "none";
    }

    function performSearch(query) {
      const normalizedQuery = query.toLowerCase().trim();
      let visibleCount = 0;

      if (normalizedQuery !== "") {
        expandAllStoresLayout();
      } else if (searchMode === "text" && !userExpandedStores) {
        restoreOriginalOrder();
      }

      container.querySelectorAll(".store-card").forEach((card) => {
        const cardIndex = parseInt(card.dataset.storeIndex, 10);
        const storeData = stores.find((store) => store.index === cardIndex);
        if (!storeData) return;

        const searchableText = [
          storeData.displayName,
          storeData.name,
          storeData.city,
          storeData.address,
          storeData.phone,
        ]
          .join(" ")
          .toLowerCase();

        const isMatch =
          normalizedQuery === "" || searchableText.includes(normalizedQuery);

        if (isMatch) {
          card.hidden = false;
          visibleCount += 1;
        } else {
          card.hidden = true;
        }
      });

      if (noResultsEl) {
        noResultsEl.style.display = visibleCount === 0 ? "block" : "none";
      }
    }

    function updateDistances(fromLat, fromLng) {
      const updateToken = ++distanceUpdateToken;
      const storesWithDistance = stores
        .filter((store) => hasValidCoords(store))
        .map((store) => ({
          ...store,
          distance: calculateDistance(fromLat, fromLng, store.lat, store.lng),
        }));

      if (!storesWithDistance.length || updateToken !== distanceUpdateToken) {
        return false;
      }

      showAllStores();
      expandAllStoresLayout();

      storesWithDistance.forEach((store) => {
        const distanceEl = container.querySelector(
          '[data-distance-for="' + store.index + '"]',
        );
        if (distanceEl) {
          distanceEl.textContent = formatDistance(store.distance);
          distanceEl.hidden = false;
        }
      });

      const sortedIndexes = new Set(
        storesWithDistance.map((store) => store.index),
      );
      const sortedStores = [
        ...storesWithDistance.sort((a, b) => a.distance - b.distance),
        ...[...stores]
          .filter((store) => !sortedIndexes.has(store.index))
          .sort((a, b) => a.index - b.index),
      ];

      appendCardsInOrder(sortedStores);

      const closest = storesWithDistance[0];
      getStoreCard(closest.index)?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
      return true;
    }

    function sortStoresByLocation(fromLat, fromLng, options = {}) {
      suppressTextSearchUntil = Date.now() + 400;
      searchMode = "location";
      if (options.activateGeoButton) geoBtn?.classList.add("is-active");
      return updateDistances(fromLat, fromLng);
    }

    async function ensureGooglePlaces() {
      if (!apiKey) return false;
      if (googleReady && window.google?.maps?.places) return true;

      await loadGoogleMapsApi(apiKey);
      googleReady = true;
      return true;
    }

    function initPlacesAutocomplete() {
      if (!window.google?.maps?.places || autocomplete) return;

      autocomplete = new google.maps.places.Autocomplete(searchInput, {
        types: ["geocode", "establishment"],
        componentRestrictions: { country: "il" },
        fields: ["geometry"],
      });

      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        const location = readLatLng(place.geometry?.location);

        if (!location) {
          geocodeSearchInput(searchInput.value);
          return;
        }

        sortStoresByLocation(location.lat, location.lng, {
          activateGeoButton: false,
        });
      });
    }

    async function prepareAddressSearch() {
      try {
        if (await ensureGooglePlaces()) {
          initPlacesAutocomplete();
          return true;
        }
      } catch (error) {
        return false;
      }

      return false;
    }

    async function geocodeSearchInput(query) {
      if (!query.trim()) return false;

      try {
        if (!(await prepareAddressSearch())) return false;

        const location = readLatLng(await geocodeQuery(query));
        if (!location) return false;

        sortStoresByLocation(location.lat, location.lng, {
          activateGeoButton: false,
        });
        return true;
      } catch (error) {
        return false;
      }
    }

    function handleGeolocation() {
      if (!navigator.geolocation) {
        alert("Geolocation is not supported by your browser");
        return;
      }

      geoBtn?.classList.add("is-loading");

      navigator.geolocation.getCurrentPosition(
        (position) => {
          geoBtn?.classList.remove("is-loading");
          searchInput.value = "";
          sortStoresByLocation(
            position.coords.latitude,
            position.coords.longitude,
            { activateGeoButton: true },
          );
        },
        () => {
          geoBtn?.classList.remove("is-loading");
          alert("Unable to get your location. Please check your browser settings.");
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000,
        },
      );
    }

    showAllBtn?.addEventListener("click", () => {
      userExpandedStores = true;
      expandAllStoresLayout();
    });

    searchInput.addEventListener("focus", () => {
      prepareAddressSearch();
    });

    searchInput.addEventListener(
      "input",
      debounce((event) => {
        if (Date.now() < suppressTextSearchUntil) return;

        if (searchMode === "location") {
          searchMode = "text";
          geoBtn?.classList.remove("is-active");
          distanceUpdateToken += 1;
          clearDistances();
          restoreOriginalOrder();
        }

        performSearch(event.target.value);
      }, 200),
    );

    searchInput.addEventListener("keydown", async (event) => {
      if (event.key !== "Enter") return;

      event.preventDefault();
      const query = searchInput.value.trim();
      if (!query) return;

      const sortedByLocation = await geocodeSearchInput(query);
      if (!sortedByLocation) {
        searchMode = "text";
        geoBtn?.classList.remove("is-active");
        clearDistances();
        restoreOriginalOrder();
        performSearch(query);
      }
    });

    clearBtn?.addEventListener("click", () => {
      searchInput.value = "";
      searchMode = "text";
      distanceUpdateToken += 1;
      geoBtn?.classList.remove("is-active");
      clearDistances();
      restoreOriginalOrder();
      performSearch("");
      searchInput.focus();
    });

    geoBtn?.addEventListener("click", handleGeolocation);

    renderAllHours();

    window.__storesSectionApis = window.__storesSectionApis || new Map();
    window.__storesSectionApis.set(config.sectionId, {
      activateNearest: handleGeolocation,
    });

    if (!window.__storesActivateNearestListener) {
      window.__storesActivateNearestListener = true;

      document.addEventListener("stores:activate-nearest", (event) => {
        const targetId = event.detail?.sectionId;
        const apis = window.__storesSectionApis;
        if (!apis?.size) return;

        if (targetId) {
          apis.get(targetId)?.activateNearest?.();
          return;
        }

        apis.forEach((api) => api.activateNearest?.());
      });
    }

    document.dispatchEvent(
      new CustomEvent("stores:ready", {
        detail: {
          sectionId: config.sectionId,
          activateNearest: handleGeolocation,
        },
      }),
    );
  }

  function boot(scope) {
    scope.querySelectorAll(DATA_SELECTOR).forEach((script) => {
      const config = parseConfig(script);
      if (config?.sectionId) initStoreSearch(config);
    });
  }

  function start() {
    boot(document);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }

  document.addEventListener("shopify:section:load", (event) => {
    boot(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    const script = event.target.querySelector(DATA_SELECTOR);
    if (!script) return;

    const config = parseConfig(script);
    if (config?.sectionId) {
      window.__storesSectionApis?.delete(config.sectionId);
    }
  });
})();
